import { createFileRoute } from "@tanstack/react-router";
import { Button } from "@/components/ui/button.tsx";
import {
  VideoWithSubtitles,
  type VideoWithSubtitlesRef,
} from "@/components/ui/VideoWithSubtitles.tsx";
import { useRef, useState } from "react";
import { FormattedMessage } from "react-intl";
import BreathingExerciseContent from "@/components/Exercises/Breathing/BreathingExerciseContent";
import breathingVideo from "@/assets/videos/exercises/breathing/breathing.webm";
import breathingPoster from "@/assets/breathing-poster.png";
import { ExerciseLayout } from "@/components/ui/exercise-layout";
import SubtitleLanguageSwitcher from "@/components/ui/SubtitleLanguageSwitcher";
import { useLocale } from "@/contexts/IntlContext";

export const Route = createFileRoute(
  "/(content)/adults/exercises/(shared)/guided-relaxation/"
)({
  component: RouteComponent,
  validateSearch: (search: Record<string, unknown>) => ({
    referrer: search?.referrer as string | undefined,
    referrerGroup: search?.referrerGroup as string | undefined,
  }),
  head: () => ({
    meta: [{ title: "Guided Relaxation & Breathing Exercise - HAMD" }],
  }),
  beforeLoad: () => ({
    title: "breathing.guidedRelax.title",
    navbar: {
      showTitle: true,
    },
  }),
});

function RouteComponent() {
  const { locale } = useLocale();
  const [showOverlay, setShowOverlay] = useState(false);
  const [subtitleLanguage, setSubtitleLanguage] = useState<"ar" | "en">(locale);
  const videoRef = useRef<VideoWithSubtitlesRef>(null);

  const subtitleTracks = [
    {
      src: "/subs/breathing.ar.vtt",
      srcLang: "ar",
      label: "العربية",
      default: subtitleLanguage === "ar",
    },
    {
      src: "/subs/breathing.en.vtt",
      srcLang: "en",
      label: "English",
      default: subtitleLanguage === "en",
    },
  ];

  const startExercise = () => {
    setShowOverlay(true);
    videoRef.current?.video?.pause();
  };

  const closeOverlay = () => {
    setShowOverlay(false);
    videoRef.current?.resetVideo();
  };

  return (
    <ExerciseLayout
      contentClassName="py-7.5 space-y-18"
      actions={
        <Button className="w-full" onClick={startExercise}>
          <FormattedMessage id="breathing.startExercise" />
        </Button>
      }
    >
      <div className="space-y-2.5">
        <div>
          <h3 className="text-h3 font-medium">
            <FormattedMessage id="breathing.intro.title" />
          </h3>
          <p className="text-small">
            <FormattedMessage id="breathing.intro.subtitle" />
          </p>
        </div>

        <SubtitleLanguageSwitcher
          subtitleLanguage={subtitleLanguage}
          onLanguageChange={setSubtitleLanguage}
        />

        <VideoWithSubtitles
          ref={videoRef}
          src={breathingVideo}
          poster={breathingPoster}
          subtitleTracks={subtitleTracks}
          activeSubtitleLanguage={subtitleLanguage}
          loop={false}
        />
      </div>

      <BreathingExerciseContent
        onClose={closeOverlay}
        onExit={closeOverlay}
        isShow={showOverlay}
      />
    </ExerciseLayout>
  );
}

export default RouteComponent;
